/**
 * Acompanhamento das notas (v7): rejeitadas, denegadas com a venda ainda confirmada, sem
 * resposta da SEFAZ (o sistema consulta antes de qualquer reenvio), falhas de processamento e
 * notas paradas na fila. As ações são as de sempre, no detalhe da nota (consultar, recalcular e
 * reenviar com o mesmo número, abandonar) e no pedido (estornar).
 */
import {
  FISCAL_ATTENTION_LABEL,
  SALES_ORIGIN_LABEL,
  formatMoney,
  type FiscalAttentionKind,
  type FiscalAttentionRow,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Badge, Button, Card, PageTitle, Spinner, Table } from "@/components/ui";
import { dateBR, dateTimeBR } from "../estoque/lib";
import { DocumentDetail } from "./DocumentDetail";
import { EmptyState, ErrorState, SummaryCard } from "@/components/patterns";
import { apiFetch, errorMessage } from "@/lib/api";
import { ATTENTION_GUIDANCE, FISCAL_KEY } from "./lib";

/** Rótulo dos cartões de resumo (no plural). */
const SUMMARY_LABEL: Record<FiscalAttentionKind, string> = {
  rejeitada: "Rejeitadas",
  denegada: "Denegadas",
  incerta: "Sem resposta da SEFAZ",
  falha: "Falhas de processamento",
  na_fila: "Paradas na fila",
};

const TONE: Record<FiscalAttentionKind, "warn" | "error"> = {
  rejeitada: "error",
  denegada: "error",
  incerta: "warn",
  falha: "error",
  na_fila: "warn",
};

export function AcompanhamentoPage() {
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({
    queryKey: [...FISCAL_KEY, "/acompanhamento"],
    queryFn: () => apiFetch<FiscalAttentionRow[]>("/fiscal/acompanhamento"),
    refetchInterval: 30_000,
  });
  return (
    <div className="space-y-6">
      <PageTitle
        title="Acompanhamento das notas"
        description="Notas que pedem atenção. A lista se atualiza sozinha a cada 30 segundos. Nenhuma nota é reenviada sem consultar antes a situação na SEFAZ."
      />
      {(list.data?.length ?? 0) > 0 && (
        <section
          aria-label="Resumo por situação"
          className="grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-4"
        >
          {(Object.keys(TONE) as FiscalAttentionKind[])
            .map((k) => ({ k, n: list.data?.filter((r) => r.kind === k).length ?? 0 }))
            .filter(({ n }) => n > 0)
            .map(({ k, n }) => (
              <SummaryCard
                key={k}
                label={SUMMARY_LABEL[k]}
                value={String(n)}
                hint={n === 1 ? "nota" : "notas"}
                tone={TONE[k]}
                emphasize
              />
            ))}
        </section>
      )}
      {open && <DocumentDetail id={open} onClose={() => setOpen(null)} />}
      <Card>
        {list.error ? (
          <ErrorState message={errorMessage(list.error)} />
        ) : list.isPending ? (
          <Spinner />
        ) : (list.data?.length ?? 0) === 0 ? (
          <EmptyState
            icon="check"
            title="Nenhuma nota pede atenção agora: tudo autorizado, cancelado ou resolvido."
            description="A lista se atualiza sozinha a cada 30 segundos."
          />
        ) : (
          <Table
            head={[
              "Situação",
              "Nota",
              "Emissão",
              "Destinatário",
              "Venda",
              "Total",
              "O que fazer",
              "",
            ]}
          >
            {list.data?.map((r) => (
              <tr key={r.documentId}>
                <td className="px-3 py-2">
                  <Badge tone={TONE[r.kind]}>{FISCAL_ATTENTION_LABEL[r.kind]}</Badge>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {dateTimeBR(r.updatedAt)}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                  {r.model} · série {r.series} · nº {r.number}
                </td>
                <td className="px-3 py-2">{dateBR(r.issueDate)}</td>
                <td className="px-3 py-2">{r.recipientName ?? "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {r.orderNumber ?? "—"}
                  {r.orderOrigin && (
                    <div className="font-sans text-muted-foreground">
                      {SALES_ORIGIN_LABEL[r.orderOrigin]}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {formatMoney(r.totalAmount)}
                </td>
                <td className="max-w-md px-3 py-2 text-xs">
                  {r.reason && <p className="mb-1 font-medium">Motivo: {r.reason}</p>}
                  <p className="text-muted-foreground">{ATTENTION_GUIDANCE[r.kind]}</p>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setOpen(r.documentId)}>
                    Abrir
                  </Button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
